import {FIELD_TYPE, TABLE_COLUMN_TYPE, TABLE_COLUMN_TYPES} from '../../core/vocabulary.js';
import {escapeHtml} from '../../core/escape-html.js';

const COLUMN_KEY_PATTERN = '^[a-z][a-z0-9_]*$';
const COLUMN_TYPE_LABELS = Object.freeze({
    [TABLE_COLUMN_TYPE.TEXT]: 'Text',
    [TABLE_COLUMN_TYPE.NUMBER]: 'Number',
    [TABLE_COLUMN_TYPE.SELECT]: 'Select',
    [TABLE_COLUMN_TYPE.DOCUMENT]: 'Document',
});

/** @returns {import('../../shell/types/client.types.js').TableColumnState} */
export function emptyTableColumn() {
    return {key: '', label: '', type: TABLE_COLUMN_TYPE.TEXT, required: false, options: ''};
}

/**
 * Column editor markup for a table field. Nothing is rendered for other types so that hidden
 * required inputs never block form submission.
 *
 * @param {import('../../core/client/template-fields.core.js').FieldConfig} config
 * @returns {string}
 */
export function tableEditorMarkup(config) {
    if (config.type !== FIELD_TYPE.TABLE) {
        return '';
    }
    const columns = config.columns ?? [];
    const lastIndex = columns.length - 1;
    const columnRows = columns.map((column, index) => `
            <div class="table-column-row" data-index="${index}">
                <input type="text" data-col-prop="key" value="${escapeHtml(column.key)}" placeholder="key" aria-label="Column key"
                    required pattern="${COLUMN_KEY_PATTERN}" title="Lowercase letters, digits and underscores; start with a letter." />
                <input type="text" data-col-prop="label" value="${escapeHtml(column.label)}" placeholder="Label" aria-label="Column label" required />
                <select data-col-prop="type" aria-label="Column type">
                    ${TABLE_COLUMN_TYPES.map((type) => `<option value="${type}" ${column.type === type ? 'selected' : ''}>${COLUMN_TYPE_LABELS[type]}</option>`).join('')}
                </select>
                <textarea data-col-prop="options" rows="3" placeholder="One option per line" aria-label="Column options"
                    ${column.type === TABLE_COLUMN_TYPE.SELECT ? 'required' : 'hidden'}>${escapeHtml(column.options)}</textarea>
                <label class="check-req">
                    <input type="checkbox" data-col-prop="required" ${column.required ? 'checked' : ''}> Required
                </label>
                <div class="table-column-actions">
                    <button type="button" class="btn btn-outline btn-sm btn-move-column" data-index="${index}" data-direction="up"
                        aria-label="Move column up" ${index === 0 ? 'disabled' : ''}><i aria-hidden="true" class="fa-solid fa-arrow-up"></i></button>
                    <button type="button" class="btn btn-outline btn-sm btn-move-column" data-index="${index}" data-direction="down"
                        aria-label="Move column down" ${index === lastIndex ? 'disabled' : ''}><i aria-hidden="true" class="fa-solid fa-arrow-down"></i></button>
                    <button type="button" class="btn btn-outline btn-sm btn-remove-column" data-index="${index}"
                        aria-label="Remove column"><i aria-hidden="true" class="fa-solid fa-trash-can"></i></button>
                </div>
            </div>
        `).join('');
    return `
            <div class="table-editor-header">
                <span class="table-editor-title">Columns</span>
                <label class="table-max-rows">
                    Max rows
                    <input type="number" data-prop="maxRows" min="1" step="1" value="${escapeHtml(config.maxRows)}" placeholder="No limit" />
                </label>
            </div>
            <p class="table-columns-empty" ${columns.length > 0 ? 'hidden' : ''}>No columns yet. A table needs at least one column.</p>
            <div class="table-columns">${columnRows}</div>
            <button type="button" class="btn btn-outline btn-sm btn-add-column"><i aria-hidden="true" class="fa-solid fa-plus"></i> Add column</button>
        `;
}

/**
 * Re-renders the column editor of a field row, hiding it for non-table fields.
 *
 * @param {HTMLElement} row
 * @param {import('../../core/client/template-fields.core.js').FieldConfig} config
 */
export function renderTableEditor(row, config) {
    const editor = row.querySelector('.table-editor');
    editor.hidden = config.type !== FIELD_TYPE.TABLE;
    editor.innerHTML = tableEditorMarkup(config);
}

/**
 * Reads the column editor of a field row. When the editor is not rendered (non-table field),
 * the stored columns and maxRows are kept so switching the type back restores them.
 *
 * @param {HTMLElement} row
 * @param {import('../../core/client/template-fields.core.js').FieldConfig | undefined} existing
 * @returns {{columns: import('../../shell/types/client.types.js').TableColumnState[], maxRows: string | number}}
 */
export function readTableConfig(row, existing) {
    const maxRowsInput = row.querySelector('[data-prop="maxRows"]');
    if (!maxRowsInput) {
        return {columns: existing?.columns ?? [], maxRows: existing?.maxRows ?? ''};
    }
    const columns = Array.from(row.querySelectorAll('.table-column-row')).map((columnRow) => ({
        key: columnRow.querySelector('[data-col-prop="key"]').value,
        label: columnRow.querySelector('[data-col-prop="label"]').value,
        type: columnRow.querySelector('[data-col-prop="type"]').value,
        required: columnRow.querySelector('[data-col-prop="required"]').checked,
        options: columnRow.querySelector('[data-col-prop="options"]').value,
    }));
    return {columns, maxRows: maxRowsInput.value};
}

/**
 * Shows and requires the options input of a column only while its type is select.
 *
 * @param {HTMLElement} columnRow
 * @param {string} type
 */
export function syncColumnTypeControls(columnRow, type) {
    const optionsInput = columnRow.querySelector('[data-col-prop="options"]');
    const isSelect = type === TABLE_COLUMN_TYPE.SELECT;
    optionsInput.hidden = !isSelect;
    optionsInput.required = isSelect;
}

/**
 * Clears the table schema error flagged on any input of a field row.
 *
 * @param {HTMLElement} row
 */
export function clearTableFieldErrors(row) {
    for (const input of row.querySelectorAll('[data-prop="type"], [data-prop="maxRows"], [data-col-prop]')) {
        input.setCustomValidity('');
    }
}

/**
 * @param {HTMLElement} row the field row of the table field
 * @param {import('../../shell/types/client.types.js').TableFieldValidationError} error
 * @returns {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} the input the error concerns
 */
export function tableFieldInput(row, {columnIndex, prop}) {
    if (columnIndex === null) {
        return row.querySelector(`[data-prop="${prop}"]`);
    }
    return row.querySelectorAll('.table-column-row')[columnIndex].querySelector(`[data-col-prop="${prop}"]`);
}
